/* eslint-disable max-lines-per-function */
import { EditIcon, LocationPinIcon } from '@change/ds-icons';

import { Button, ChangeDot, HeroLayout, Link, Text, ThemeProvider } from '@change/ds-core-ui/next';

import { Translate, TranslateHtml, TranslatePluralHtml } from '@change-corgi/core/react/i18n';

import { GooglePlacesProvider } from 'src/app/shared/hooks/googlePlaces';
import { isLoaded } from 'src/app/shared/utils/async';

import { EditLocationModal } from './components/EditLocationModal';
import { useLocalSection } from './hooks/useLocalSection';

export function LocalSectionSunrise() {
	const {
		data: { locationData, isModalOpen },
		actions,
	} = useLocalSection();

	if (!isLoaded(locationData)) return null;

	return (
		<ThemeProvider>
			<HeroLayout
				reverseMd
				reverseSm
				data-testid="homepage-sunrise-local-section"
				variant="1/2"
				as="section"
				className="text-typography-strong bg-gradient-background-light-3"
				media={
					<div className="flex h-full items-center bg-[url(https://static.change.org/homepageV3/map-pattern-sunrise-transparent.svg)] bg-cover bg-center p-4 md:p-12">
						<div className="flex flex-col gap-4">
							<StatItem testId="signatures">
								<TranslatePluralHtml
									value="corgi.homepage.local_section.stats.signatures"
									count={locationData.value.signaturesCount}
									replacements={{
										signatureCountLocalized: locationData.value.signaturesCountLocalized,
									}}
								/>
							</StatItem>

							<StatItem testId="petitions">
								<TranslatePluralHtml
									value="corgi.homepage.local_section.stats.petitions"
									count={locationData.value.petitionsCount}
									replacements={{
										petitionCountLocalized: locationData.value.petitionsCountLocalized,
									}}
								/>
							</StatItem>

							<StatItem testId="decision-makers">
								<TranslatePluralHtml
									value="corgi.homepage.local_section.stats.decision_makers"
									count={locationData.value.decisionMakersCount}
									replacements={{
										decisionMakerCountLocalized: locationData.value.decisionMakersCountLocalized,
									}}
								/>
							</StatItem>
						</div>
					</div>
				}
			>
				<div className="p-8 sm:p-6 md:p-12">
					<div className="space-y-6 md:p-6">
						<div className="space-y-4">
							{/* Happening In Location */}
							<Text as="h2" variant="heading" size="large" className="m-0">
								<Translate
									key={locationData.value.slug}
									value="corgi.homepage.local_section.title"
									replacements={{ location: locationData.value.shortName }}
								/>
							</Text>

							{/* Contains "Showing Results for Location" */}
							<div className="flex space-x-1">
								<LocationPinIcon />
								{/* Showing results for */}
								<GooglePlacesProvider>
									<EditLocationModal
										trigger={
											<Text size="large">
												<TranslateHtml
													value="corgi.homepage.local_section.subheading"
													replacements={{
														location: (
															<span className="inline-flex items-center">
																<Link
																	as="button"
																	trailingIcon={<EditIcon className="text-icon-strong" />}
																	data-test-id="edit-location-link"
																	data-qa="edit-location-link"
																	onClick={() => actions.handleEditLocationClick(locationData.value)}
																>
																	{locationData.value.name}
																</Link>
															</span>
														),
													}}
												/>
											</Text>
										}
										isOpen={isModalOpen}
										onClose={actions.handleCloseModal}
									/>
								</GooglePlacesProvider>
							</div>
						</div>

						{/* "Explore Local Petitions" Button */}
						<Button
							as="a"
							href={locationData.value.localPageUrl}
							variant="secondary"
							size="large"
							data-test-id="homepage-v2-local-section-explore-button"
							data-qa="homepage-v2-local-section-explore-button"
							onClick={() => actions.onLocalSectionClick(locationData.value)}
						>
							<Translate value="corgi.homepage.local_section.explore_button" />
						</Button>
					</div>
				</div>
			</HeroLayout>
		</ThemeProvider>
	);
}

const StatItem: React.FC<{ children: React.ReactNode; testId: string }> = ({ children, testId }) => (
	<div className="flex items-center gap-2">
		<ChangeDot size="small" />

		<Text variant="body" size="x-large" data-testid={`local-section-${testId}-count-plural`}>
			{children}
		</Text>
	</div>
);
